import { useState } from "react";
import { Plus } from "lucide-react";

export const PRESET_IMAGES = [
  { label: "Indoor Arena", url: "https://images.unsplash.com/photo-1635842939844-1fbf6bea8e78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB0ZW5uaXMlMjBmYWNpbGl0eXxlbnwwfHx8fDE3ODc5MDI1ODl8MA&ixlib=rb-4.1.0&q=85" },
  { label: "Red Clay", url: "https://images.unsplash.com/photo-1620742820748-87c09249a72a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHx0ZW5uaXMlMjBjb3VydCUyMGRhcmt8ZW58MHx8fHwxNzg3OTAyNTg5fDA&ixlib=rb-4.1.0&q=85" },
  { label: "Night Court", url: "https://images.unsplash.com/photo-1692288720754-743fbd1f2155?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHx0ZW5uaXMlMjBjb3VydCUyMGRhcmt8ZW58MHx8fHwxNzg3OTAyNTg5fDA&ixlib=rb-4.1.0&q=85" },
  { label: "Outdoor Aerial", url: "https://images.unsplash.com/photo-1499510318569-1a3d67dc3976?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB0ZW5uaXMlMjBmYWNpbGl0eXxlbnwwfHx8fDE3ODc5MDI1ODl8MA&ixlib=rb-4.1.0&q=85" },
  { label: "In Action", url: "https://images.unsplash.com/photo-1554068865-24cecd4e34b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHx0ZW5uaXMlMjBwbGF5ZXIlMjBhY3Rpb258ZW58MHx8fHwxNzg3OTAyNTg5fDA&ixlib=rb-4.1.0&q=85" },
];

const inputCls = "bg-[#1a1a1a] border border-white/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#ccff00]/60";
const labelCls = "text-[11px] uppercase tracking-widest text-white/40";

export const AddCourtForm = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ name: "", surface: "", type: "", price: 30, tagline: "", description: "", features: "" });
  const [image, setImage] = useState(PRESET_IMAGES[0].url);
  const [customUrl, setCustomUrl] = useState("");
  const set = (k) => (e) => setDraft({ ...draft, [k]: k === "price" ? Number(e.target.value) : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const ok = await onAdd({
      ...draft,
      image: customUrl.trim() || image,
      features: draft.features.split(",").map((f) => f.trim()).filter(Boolean),
    });
    setSaving(false);
    if (ok) {
      setDraft({ name: "", surface: "", type: "", price: 30, tagline: "", description: "", features: "" });
      setCustomUrl("");
      setOpen(false);
    }
  };

  if (!open)
    return (
      <button onClick={() => setOpen(true)} data-testid="admin-add-court-btn" className="w-full border border-dashed border-[#ccff00]/40 text-[#ccff00] uppercase tracking-widest text-sm py-4 rounded-md hover:bg-[#ccff00]/5 transition-colors inline-flex items-center justify-center gap-2">
        <Plus size={16} /> Add a new court
      </button>
    );

  return (
    <form onSubmit={submit} className="bg-[#0f0f10] border border-[#ccff00]/40 rounded-md p-6 space-y-5" data-testid="admin-add-court-form">
      <h3 className="font-display uppercase text-xl tracking-tight">New Court</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <label className="flex flex-col gap-1.5"><span className={labelCls}>Name *</span>
          <input required value={draft.name} onChange={set("name")} data-testid="new-court-name-input" className={inputCls} /></label>
        <label className="flex flex-col gap-1.5"><span className={labelCls}>Surface *</span>
          <input required value={draft.surface} onChange={set("surface")} placeholder="e.g. Hard — Acrylic" data-testid="new-court-surface-input" className={inputCls} /></label>
        <label className="flex flex-col gap-1.5"><span className={labelCls}>Type *</span>
          <input required value={draft.type} onChange={set("type")} placeholder="Indoor / Outdoor" data-testid="new-court-type-input" className={inputCls} /></label>
        <label className="flex flex-col gap-1.5"><span className={labelCls}>Price ($/hr) *</span>
          <input required type="number" min="1" value={draft.price} onChange={set("price")} data-testid="new-court-price-input" className={inputCls} /></label>
        <label className="flex flex-col gap-1.5 sm:col-span-2 lg:col-span-4"><span className={labelCls}>Tagline</span>
          <input value={draft.tagline} onChange={set("tagline")} data-testid="new-court-tagline-input" className={inputCls} /></label>
        <label className="flex flex-col gap-1.5 sm:col-span-2 lg:col-span-4"><span className={labelCls}>Description</span>
          <textarea rows={2} value={draft.description} onChange={set("description")} className={inputCls} /></label>
        <label className="flex flex-col gap-1.5 sm:col-span-2 lg:col-span-4"><span className={labelCls}>Features (comma separated)</span>
          <input value={draft.features} onChange={set("features")} placeholder="Floodlights, Ball machine, Shaded seating" className={inputCls} /></label>
      </div>
      <div>
        <p className={`${labelCls} mb-3`}>Photo — pick from gallery or paste a URL</p>
        <div className="flex gap-3 flex-wrap">
          {PRESET_IMAGES.map((p) => (
            <button type="button" key={p.url} onClick={() => { setImage(p.url); setCustomUrl(""); }} data-testid={`preset-image-${p.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`relative w-24 h-16 rounded-md overflow-hidden border-2 transition-colors ${image === p.url && !customUrl ? "border-[#ccff00]" : "border-transparent opacity-70 hover:opacity-100"}`}>
              <img src={p.url} alt={p.label} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
        <input value={customUrl} onChange={(e) => setCustomUrl(e.target.value)} placeholder="Optional: custom image URL" data-testid="new-court-image-url-input" className={`${inputCls} w-full mt-3`} />
      </div>
      <div className="flex gap-3">
        <button type="submit" disabled={saving} data-testid="new-court-submit-btn" className="bg-[#ccff00] text-black font-bold uppercase tracking-wider text-xs px-8 py-3 rounded-full hover:bg-[#a3e635] transition-colors disabled:opacity-50">
          {saving ? "Adding…" : "Add court"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="border border-white/20 text-white/70 uppercase tracking-wider text-xs px-6 py-3 rounded-full hover:text-white transition-colors">Cancel</button>
      </div>
    </form>
  );
};

export const AddCoachForm = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ name: "", role: "", specialty: "", bio: "", price: 60, experience: "" });
  const set = (k) => (e) => setDraft({ ...draft, [k]: k === "price" ? Number(e.target.value) : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const ok = await onAdd(draft);
    setSaving(false);
    if (ok) {
      setDraft({ name: "", role: "", specialty: "", bio: "", price: 60, experience: "" });
      setOpen(false);
    }
  };

  if (!open)
    return (
      <button onClick={() => setOpen(true)} data-testid="admin-add-coach-btn" className="w-full border border-dashed border-[#ccff00]/40 text-[#ccff00] uppercase tracking-widest text-sm py-4 rounded-md hover:bg-[#ccff00]/5 transition-colors inline-flex items-center justify-center gap-2">
        <Plus size={16} /> Add a new coach
      </button>
    );

  return (
    <form onSubmit={submit} className="bg-[#0f0f10] border border-[#ccff00]/40 rounded-md p-6 space-y-5" data-testid="admin-add-coach-form">
      <h3 className="font-display uppercase text-xl tracking-tight">New Coach</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <label className="flex flex-col gap-1.5"><span className={labelCls}>Name *</span>
          <input required value={draft.name} onChange={set("name")} data-testid="new-coach-name-input" className={inputCls} /></label>
        <label className="flex flex-col gap-1.5"><span className={labelCls}>Role *</span>
          <input required value={draft.role} onChange={set("role")} placeholder="e.g. Serve Specialist" data-testid="new-coach-role-input" className={inputCls} /></label>
        <label className="flex flex-col gap-1.5"><span className={labelCls}>Specialty *</span>
          <input required value={draft.specialty} onChange={set("specialty")} data-testid="new-coach-specialty-input" className={inputCls} /></label>
        <label className="flex flex-col gap-1.5"><span className={labelCls}>Price ($/hr) *</span>
          <input required type="number" min="1" value={draft.price} onChange={set("price")} data-testid="new-coach-price-input" className={inputCls} /></label>
        <label className="flex flex-col gap-1.5 sm:col-span-2"><span className={labelCls}>Experience</span>
          <input value={draft.experience} onChange={set("experience")} placeholder="e.g. 10 yrs coaching" className={inputCls} /></label>
        <label className="flex flex-col gap-1.5 sm:col-span-2 lg:col-span-4"><span className={labelCls}>Bio</span>
          <textarea rows={2} value={draft.bio} onChange={set("bio")} className={inputCls} /></label>
      </div>
      <div className="flex gap-3">
        <button type="submit" disabled={saving} data-testid="new-coach-submit-btn" className="bg-[#ccff00] text-black font-bold uppercase tracking-wider text-xs px-8 py-3 rounded-full hover:bg-[#a3e635] transition-colors disabled:opacity-50">
          {saving ? "Adding…" : "Add coach"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="border border-white/20 text-white/70 uppercase tracking-wider text-xs px-6 py-3 rounded-full hover:text-white transition-colors">Cancel</button>
      </div>
    </form>
  );
};
